// src/components/VideoPlayerModal.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, functions, httpsCallable, doc, onSnapshot, getDoc, setDoc } from '../firebase';
import { collection, query, orderBy, limit } from 'firebase/firestore';
import LikeButton from './LikeButton.jsx';
import RoleBadge from './RoleBadge.jsx';
import RoastTokenVault from './RoastTokenVault';

// --- THE NVA TOKEN CATALOG ---
const GIFT_TOKENS = [
    { id: 'spotlight', name: 'Warm Spotlight', price: 500, actorReceives: 425, platformFee: 75, icon: '🔦' },
    { id: 'popcorn', name: 'Golden Popcorn', price: 1000, actorReceives: 850, platformFee: 150, icon: '🍿' },
    { id: 'flare', name: 'Rainbow Flare', price: 2500, actorReceives: 2125, platformFee: 375, icon: '🌈' },
    { id: 'chair', name: "Director's Chair", price: 5000, actorReceives: 4250, platformFee: 750, icon: '🎬' },
    { id: 'producer', name: 'The Executive Producer', price: 10000, actorReceives: 8500, platformFee: 1500, icon: '💎' },
];

const MMG_NUMBER = "592-672-3204";

// Generate vibrant, unique colors for user tags
const generateColorFromId = (id) => {
    if (!id) return '#FFFFFF';
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xFF;
        value = Math.floor(128 + (value % 128)); 
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const appId = 'production-app-id';

const extractVideoInfo = (url) => {
    if (!url || typeof url !== 'string') return { embedUrl: null, isVertical: false, platform: 'unknown' };

    // --- NEW FACEBOOK LOGIC ---
    if (url.includes('facebook.com') || url.includes('fb.watch')) {
        const encodedFbUrl = encodeURIComponent(url);
        // Returns the special player URL that Facebook requires for embedding.
        return { embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodedFbUrl}&show_text=false&autoplay=true&mute=1`, isVertical: false, platform: 'facebook' };
    }
    // --- END NEW LOGIC ---

    const ytShortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (ytShortsMatch) {
        return { embedUrl: `https://www.youtube.com/embed/${ytShortsMatch[1]}`, isVertical: true, platform: 'youtube' };
    }
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    if (ytMatch) {
        return { embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`, isVertical: false, platform: 'youtube' };
    }
    const tiktokMatch = url.match(/tiktok\.com\/.*\/video\/(\d+)/);
    if (tiktokMatch) {
        return { embedUrl: `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}`, isVertical: true, platform: 'tiktok' };
    }
    const isNativeMedia = /\.(mp4|webm|ogg|mov)$/i.test(url) || url.includes('firebasestorage.googleapis.com') || url.includes('r2.dev');
    return { embedUrl: url, isVertical: false, platform: isNativeMedia ? 'native' : 'unknown' };
};

const VideoPlayerModal = ({ videoUrl, onClose, contentItem, currentUser, viewerProfile, showMessage, openCommentsProp }) => {
    const [liveContentItem, setLiveContentItem] = useState(contentItem);
    const [showEarningsConfirm, setShowEarningsConfirm] = useState(false);
    const [creatorProfile, setCreatorProfile] = useState(null);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const viewCountedRef = useRef(false);
    const itemType = useMemo(() => liveContentItem?.eventTitle ? 'event' : 'content', [liveContentItem]);
    const { embedUrl, isVertical, platform } = useMemo(() => extractVideoInfo(videoUrl), [videoUrl]);

    // Embedded Comments Engine State
    const [comments, setComments] = useState([]);
    const [newCommentText, setNewCommentText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);
    const commentsEndRef = useRef(null);

    // Threading & Pagination State
    const [commentLimit, setCommentLimit] = useState(10);
            const [expandedThreads, setExpandedThreads] = useState({});
            const [showEmojiPicker, setShowEmojiPicker] = useState(false);
            
            const EMOJI_LIST = Array.from(new Set(['😀','😃','😄','😁','😆','😅','😂','🤣','🥲','☺️','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚','😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🥸','🤩','🥳','😏','😒','😞','😔','😟','😕','🙁','☹️','😣','😖','😫','😩','🥺','😢','😭','😤','😠','😡','🤬','🤯','😳','🥵','🥶','😱','😨','😰','😥','😓','🤗','🤔','🤭','🤫','🤥','😶','😐','😑','😬','🙄','😯','😦','😧','😮','😲','🥱','😴','🤤','😪','😵','🤐','🥴','🤢','🤮','🤧','😷','🤒','🤕','🤑','🤠','😈','👿','👹','👺','🤡','💩','👻','💀','☠️','👽','👾','🤖','🎃','😺','😸','😹','😻','😼','😽','🙀','😿','😾','👋','🤚','🖐','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💅','🤳','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🫀','🫁','🧠','🦷','🦴','👀','👁','舌','👄','💋','🩸','❤️','🔥','✨','🌟','💯','💦','💨','💫','💥','💢','🎉','🎊','🎈','🎂','🍿','🎬','🎵','🎶','🎸','🎹','🎺','🎻','🥁','📱','💻','🖥','🖨','🖱','🖲','🕹','🗜','💽','💾','💿','📀','📼','📷','📸','📹','🎥','📽','🎞','📞','☎️','📟','📠','📺','📻','🎙','🎚','🎛','🧭','⏱','⏲','⏰','🕰','⌛️','⏳','📡','🔋','🔌','💡','🔦','🕯','🪔','🧯','🛢','💸','💵','💴','💶','💷','🪙','💰','💳','💎','⚖️','🪜','🧰','🪛','🔧','🔨','⚒','🛠','⛏','🪚','🔩','⚙️','🪤','🧱','⛓','🧲','🔫','💣','🧨','🪓','🔪','🗡','⚔️','🛡','🚬','⚰️','🪦','🏺','🔮','📿','🧿','💈','⚗️','🔭','🔬','🕳','🩹','🩺','💊','💉','🧬','🦠','🧫','🧪','🌡','🧹','🪠','🧺','🧻','🚽','🚰','🚿','🛁','🛀','🧼','🧽','🪒','🧴','🛎','🔑','🗝','🚪','🪑','🛋','🛏','🛌','🧸','🪆','🖼','🪞','🪟','🛍','🛒','🎁','🇬Y','🇺🇸','🇬🇧','🇨🇦','🇯🇲','🇹🇹','🇧🇧','🇧🇸','🇿🇦','🇳🇬','🇬🇭','🇯🇵','🇧🇷','🇫🇷','🇩🇪','🇮🇹','🇪🇸','🇲🇽','🇮🇳','🇳🇱']));
    
    const toggleThread = (id, e) => {
        if (e) e.stopPropagation();
        setExpandedThreads(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- GIFT MODAL STATE ---
    const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
    const [giftTokens, setGiftTokens] = useState(GIFT_TOKENS);
    const [selectedToken, setSelectedToken] = useState(GIFT_TOKENS[0]);
    const [isAnonymous, setIsAnonymous] = useState(false);

    // Dynamic database subscription
    useEffect(() => {
        if (!isGiftModalOpen) return;
        const unsub = onSnapshot(doc(db, "settings", "tokenEconomics"), (snap) => {
            if (snap.exists() && snap.data().giftTokens) {
                const gTokens = snap.data().giftTokens;
                setGiftTokens(gTokens);
                setSelectedToken(prev => gTokens.find(t => t.id === prev.id) || gTokens[0]);
            }
        });
        return () => unsub();
    }, [isGiftModalOpen]);
    
    const [isSubmittingGift, setIsSubmittingGift] = useState(false);
        const [submitSuccess, setSubmitSuccess] = useState(false);
        const [successMode, setSuccessMode] = useState('tokens');
        const [flameAnimId, setFlameAnimId] = useState(null);
        const [isVaultOpen, setIsVaultOpen] = useState(false);
        const [comboCount, setComboCount] = useState(0);
        const comboTimerRef = useRef(null);

        const handleQuickTip = async (e) => {
            e.stopPropagation();
            if ((viewerProfile?.tokenBalance || 0) < 1) {
                setIsVaultOpen(true);
                return;
            }
            setFlameAnimId('quick_tip');

            // TikTok Style Combo Counter logic (2.5s window)
            if (comboTimerRef.current) clearTimeout(comboTimerRef.current);
            const nextCombo = comboCount + 1;
            setComboCount(nextCombo);
            comboTimerRef.current = setTimeout(() => {
                setComboCount(0);
            }, 2500);

            // Trigger screen-level un-clipped floating flame animation
            if (e.clientX && e.clientY) {
                const flame = document.createElement('div');
                flame.className = 'floating-flame-screen';
                flame.innerText = '🔥';
                flame.style.left = `${e.clientX}px`;
                flame.style.top = `${e.clientY}px`;
                document.body.appendChild(flame);
                setTimeout(() => flame.remove(), 1000);
            }

            const targetName = creatorProfile?.creatorName || liveContentItem?.creatorName || 'the creator';
            try {
                const tipFunc = httpsCallable(functions, 'sendTokenTip');
                await tipFunc({
                    targetUserId: liveContentItem.creatorId,
                    giftName: 'Fire Coin Tip',
                    amount: 1,
                    eventId: itemType === 'event' ? liveContentItem.id : null,
                    isAnonymous: false
                });
                showMessage(`🔥 Tipped ${targetName}! ${nextCombo > 1 ? `(x${nextCombo} Combo!)` : ''}`);
            } catch (err) {
                showMessage("Tip failed: " + err.message);
            } finally {
                setTimeout(() => setFlameAnimId(null), 850);
            }
        };

    {/* Legacy Tip Catalog Migrated to GlobalTippingModal */}

    // Resolve exact database comment path based on VOD/Event Type
    const collectionPath = useMemo(() => {
        if (!liveContentItem?.id) return null;
        const targetId = liveContentItem.originalContentId || liveContentItem.id;
        // THE FIX: Reads live chatMessages for published VODs that originated from live events
        return liveContentItem.originalContentId
            ? `events/${targetId}/chatMessages`
            : (itemType === 'event' ? `events/${targetId}/comments` : `artifacts/production-app-id/public/data/content_items/${targetId}/comments`);
    }, [liveContentItem, itemType]);

    // Real-time listener with Pagination (Limit 10)
    useEffect(() => {
        if (!collectionPath || !currentUser) return;
        const commentsRef = collection(db, collectionPath);
        // Descending brings the newest top-level comments to the top (YouTube Style)
        const q = query(commentsRef, orderBy('createdAt', 'desc'), limit(commentLimit));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [collectionPath, currentUser, commentLimit]);

    const handleSubmitComment = async () => {
        if (!newCommentText.trim()) return;
        setIsSubmitting(true);
        try {
            const postCommentFunction = httpsCallable(functions, 'postComment');
            await postCommentFunction({
                itemId: liveContentItem.originalContentId || liveContentItem.id,
                itemType: liveContentItem.originalContentId ? 'event' : itemType,
                text: newCommentText.trim(),
                replyTo: replyingTo || null,
                subcollection: liveContentItem.originalContentId ? "chatMessages" : "comments"
            });
            setNewCommentText('');
            setReplyingTo(null);
        } catch (error) {
            console.error(error);
            showMessage("Failed to submit comment.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const delCommentFunction = httpsCallable(functions, 'deleteComment');
            await delCommentFunction({
                itemId: liveContentItem.originalContentId || liveContentItem.id,
                itemType: liveContentItem.originalContentId ? 'event' : itemType,
                commentId: commentId,
                subcollection: liveContentItem.originalContentId ? "chatMessages" : "comments"
            });
        } catch (error) {
            console.error(error);
            showMessage("Failed to delete comment.");
        }
    };

    const handleLikeComment = async (commentId) => {
        if (!currentUser) return showMessage("Please log in to like comments.");
        try {
            const likeCommentFunction = httpsCallable(functions, 'likeComment');
            await likeCommentFunction({
                itemId: liveContentItem.originalContentId || liveContentItem.id,
                itemType: liveContentItem.originalContentId ? 'event' : itemType,
                commentId: commentId,
                subcollection: liveContentItem.originalContentId ? "chatMessages" : "comments"
            });
        } catch (error) {
            console.error(error);
            showMessage("Failed to like comment.");
        }
    };

    const isSharingRef = useRef(false);
    const handleShareVideo = async (e) => {
        e.stopPropagation();
        if (isSharingRef.current) return;
        isSharingRef.current = true;

        const shareUrl = `${window.location.origin}/content/${liveContentItem?.id}`;
        const text = `Check out "${liveContentItem?.title || 'this video'}" on NVA Network!`;

        const fallbackCopy = async () => {
            try {
                await navigator.clipboard.writeText(`${text} ${shareUrl}`);
                showMessage("Link copied to clipboard!");
            } catch {
                showMessage("Failed to copy link.");
            }
        };

        if (navigator.share) {
            try {
                await navigator.share({ title: liveContentItem?.title || 'NVA Content', text: text, url: shareUrl });
            } catch (err) {
                // If user didn't intentionally cancel, or if Windows share sheet hung, fallback to clipboard
                if (err.name !== 'AbortError') {
                    await fallbackCopy();
                }
            } finally {
                isSharingRef.current = false;
            }
        } else {
            await fallbackCopy();
            isSharingRef.current = false;
        }
    };


    useEffect(() => {
        if (!contentItem?.id) return;
        const itemId = contentItem.originalContentId || contentItem.id;
        const currentAppId = appId; 
        const docPath = contentItem?.eventTitle ? `events/${itemId}` : `artifacts/${currentAppId}/public/data/content_items/${itemId}`;
        const unsubContent = onSnapshot(doc(db, docPath), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setLiveContentItem(prev => ({ ...prev, id: docSnap.id, ...data }));
                if (data.creatorId) {
                    getDoc(doc(db, "creators", data.creatorId)).then(creatorSnap => {
                        if (creatorSnap.exists()) setCreatorProfile({ id: creatorSnap.id, ...creatorSnap.data() });
                    });
                }
            }
        });
        return () => unsubContent();
    }, [contentItem?.id, contentItem?.originalContentId, contentItem?.eventTitle]);

    // Broadcast Modal State to freeze Home Screen background videos
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('nva_modal_toggled', { detail: true }));
        return () => window.dispatchEvent(new CustomEvent('nva_modal_toggled', { detail: false }));
    }, []);

    useEffect(() => {
        if (!liveContentItem || !liveContentItem.id || !currentUser || viewCountedRef.current || currentUser.uid === liveContentItem.creatorId) return;
        const timer = setTimeout(async () => {
            viewCountedRef.current = true;
            try {
                const incrementViewFunction = httpsCallable(functions, 'incrementViewCount');
                await incrementViewFunction({ itemId: liveContentItem.id, itemType });
            } catch (err) {
                console.error("View increment failed", err);
            }
        }, 10000);
        return () => clearTimeout(timer);
    }, [liveContentItem, currentUser, itemType]);

    const hasTriggeredComments = useRef(false);
    useEffect(() => {
        if (openCommentsProp && liveContentItem?.id && !hasTriggeredComments.current) {
            hasTriggeredComments.current = true;
            const timer = setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openCommentsModal', { detail: { item: liveContentItem, itemType: itemType } }));
            }, 500); 
            return () => clearTimeout(timer);
        }
    }, [openCommentsProp, liveContentItem?.id, itemType]);

    // SURGICAL FIX: Safe navigation bridge to prevent race conditions with history stack popping
    const executeSafeNavigation = (eventName, eventDetail) => {
        let popStateResolved = false;
        const resolveNavigation = () => {
            if (popStateResolved) return;
            popStateResolved = true;
            window.removeEventListener('popstate', resolveNavigation);
            
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent(eventName, { detail: eventDetail }));
            }, 20); 
        };
        
        window.addEventListener('popstate', resolveNavigation);
        onClose(); 
        setTimeout(resolveNavigation, 300);
    };

    if (!videoUrl) return null;

    const displayViewCount = itemType === 'event' ? liveContentItem?.totalViewCount : liveContentItem?.viewCount;

    // NVA TICKETING SYSTEM: Local Client Check (0 Database Reads)
    const isAdminOrCreator = viewerProfile?.role === 'admin' || viewerProfile?.role === 'authority' || currentUser?.uid === liveContentItem?.creatorId;
    const hasTicket = !!viewerProfile?.purchasedTickets?.[liveContentItem?.id];
    const isLocked = itemType === 'event' && liveContentItem?.isTicketed && !hasTicket && !isAdminOrCreator;
    const now = new Date();
    const eventTime = liveContentItem?.scheduledStartTime?.toDate ? liveContentItem.scheduledStartTime.toDate() : new Date();
    const isLive = eventTime <= now && liveContentItem?.status !== 'completed';

    const memoizedPlayer = useMemo(() => {
        if (isLocked) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-[#0A0A0A] p-6 text-center shadow-inner relative" style={{ aspectRatio: '16 / 9', backgroundImage: `linear-gradient(rgba(10,10,10,0.85), rgba(10,10,10,0.98)), url(${liveContentItem?.thumbnailUrl || 'https://placehold.co/1280x720/111/333?text=NVA+Box+Office'})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid #FFD700', borderRadius: '8px' }}>
                    <span className="text-6xl mb-4" style={{ filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.5))' }}>🎟️</span>
                    <h3 style={{ color: '#FFD700', fontSize: '24px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 10px 0' }}>Box Office Ticket Required</h3>
                    <p className="text-white text-sm md:text-base mb-6 max-w-md font-bold">
                        {isLive ? '🔴 This premiere event is currently LIVE.' : `⏳ Premieres ${eventTime.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`} <br/>
                        Secure your pledge ticket to unlock playback.
                    </p>
                    <button 
                        className="gift-btn-pink-glow" 
                        style={{ height: '48px', padding: '0 24px', fontSize: '16px', background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', borderColor: '#FFD700', boxShadow: '0 0 15px rgba(255,215,0,0.2)' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            window.dispatchEvent(new CustomEvent('openSubscriptionModal', { detail: liveContentItem }));
                        }}
                    >
                        <span>PURCHASE TICKET (${liveContentItem?.ticketPrice || 0} USD)</span>
                    </button>
                </div>
            );
        }

        const separator = embedUrl && embedUrl.includes('?') ? '&' : '?';
        // Isolate Facebook URLs so YouTube-only params (controls/disablekb) do not corrupt FB autoplay
        const secureUrl = (platform === 'facebook') 
            ? embedUrl 
            : (embedUrl ? `${embedUrl}${separator}autoplay=1&controls=${isAdminOrCreator ? '1' : '0'}&disablekb=${isAdminOrCreator ? '0' : '1'}` : embedUrl);

        if (platform === 'native') {
            return (
                <video
                    ref={(el) => {
                        if (el) {
                            el.currentTime = 0;
                            const playPromise = el.play();
                            if (playPromise !== undefined) playPromise.catch(() => {});
                        }
                    }}
                    src={embedUrl}
                    className="w-full h-full border-none"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                    controls={true}
                    autoPlay
                    playsInline
                />
            );
        }

        return (
            <iframe
                src={secureUrl}
                className="w-full h-full border-none"
                style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                }}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                title="Embedded Video Content"
            />
        );
    }, [embedUrl, platform, isVertical, isLocked, liveContentItem?.thumbnailUrl, liveContentItem?.ticketPrice, liveContentItem?.id, liveContentItem?.startTime, isLive, itemType, isAdminOrCreator]);

    return (
        <div className="videoModalOverlay flex justify-center items-center" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.8)' }}>
            <style>{`
                .premium-glass-info { background: linear-gradient(to bottom, rgba(15,15,15,0.7), rgba(5,5,5,0.95)); backdrop-filter: blur(25px); border-top: 1px solid rgba(255,255,255,0.08); border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; box-shadow: inset 0 1px 1px rgba(255,255,255,0.05); }
                
                /* --- FLOATING ANIMATIONS & TIKTOK COMBO COUNTER --- */
                @keyframes floatUpAndFade {
                    0% { transform: translateY(0) scale(1); opacity: 1; }
                    100% { transform: translateY(-100px) scale(1.5); opacity: 0; }
                }
                .floating-heart {
                    position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); font-size: 24px;
                    animation: floatUpAndFade 1s ease-out forwards; pointer-events: none; z-index: 100;
                }
                @keyframes floatUpAndFadeScreen {
                    0% { transform: translate(-50%, 0) scale(1); opacity: 1; }
                    100% { transform: translate(-50%, -130px) scale(1.8); opacity: 0; }
                }
                .floating-flame-screen {
                    position: fixed;
                    pointer-events: none;
                    z-index: 99999;
                    font-size: 30px;
                    animation: floatUpAndFadeScreen 1s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                    filter: drop-shadow(0 0 12px #FF4500);
                }
                @keyframes comboPop {
                    0% { transform: scale(0.4) rotate(-12deg); opacity: 0; }
                    50% { transform: scale(1.4) rotate(6deg); opacity: 1; }
                    100% { transform: scale(1) rotate(0deg); opacity: 1; }
                }
                .tiktok-combo-badge {
                    position: absolute;
                    top: -18px;
                    right: -6px;
                    background: linear-gradient(135deg, #FFD700 0%, #FF4500 100%);
                    color: #000;
                    font-size: 11px;
                    font-weight: 900;
                    padding: 2px 7px;
                    border-radius: 12px;
                    box-shadow: 0 0 12px #FF4500;
                    animation: comboPop 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                    pointer-events: none;
                    z-index: 100;
                    border: 1px solid #FFF;
                }
                @keyframes flameBurst { 
                    0% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 5px #FF4500); } 
                    50% { transform: scale(1.8) rotate(-10deg); filter: drop-shadow(0 0 20px #FFD700) drop-shadow(0 0 35px #FF4500); } 
                    100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 5px #FF4500); } 
                }
                .flame-burst-anim { animation: flameBurst 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; z-index: 50; }
                
                /* Clean Gifting Button with NO GLOW and WHITE TEXT */
                .gift-btn-clean {
                    background: rgba(255, 255, 255, 0.05) !important;
                    color: #FFFFFF !important;
                    font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em;
                    border: 1px solid rgba(255, 255, 255, 0.2) !important; border-radius: 8px; padding: 0 14px; height: 36px;
                    display: flex; align-items: center; gap: 8px; cursor: pointer;
                    transition: all 0.25s ease-in-out;
                }
                .gift-btn-clean:hover {
                    background: rgba(255, 255, 255, 0.1) !important;
                    transform: translateY(-1px);
                }

                /* ====== THE MODAL OVERLAY PORTAL ====== */
                .gift-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center; z-index: 1200; padding: 16px; }
.gift-modal { background: linear-gradient(180deg, #111111 0%, #050505 100%); border: 1px solid rgba(255,215,0,0.15); border-radius: 24px; width: 100%; max-width: 440px; max-height: 90vh; overflow-y: auto; padding: 32px; box-shadow: 0 30px 60px rgba(0,0,0,0.9), inset 0 1px 1px rgba(255,255,255,0.05); text-align: left; }

.modal-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
.modal-close { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #FFF; font-size: 18px; cursor: pointer; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 50%; transition: all 0.2s; }
.modal-close:hover { background: #DC3545; border-color: #DC3545; transform: scale(1.05); }

/* Sleek Token Cards */
.token-card { display: flex; align-items: center; gap: 16px; padding: 14px 16px; border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; margin-bottom: 12px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); background: rgba(25,25,25,0.4); text-align: left; position: relative; overflow: hidden; }
.token-card:hover { background: rgba(255,215,0,0.03); border-color: rgba(255,215,0,0.3); transform: translateY(-2px); }
.token-card.selected { background: linear-gradient(90deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.02) 100%); border-color: #FFD700; box-shadow: 0 0 20px rgba(255,215,0,0.1); }
.token-card.selected::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: #FFD700; border-radius: 4px 0 0 4px; }

.token-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0; background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(0,0,0,0.5)); border: 1px solid rgba(255,255,255,0.05); box-shadow: inset 0 2px 5px rgba(255,255,255,0.1); }
.token-info { flex: 1; }
.token-name { font-size: 15px; font-weight: 800; color: #FFFFFF; margin: 0 0 4px 0; letter-spacing: 0.02em; }
.token-breakdown { font-size: 10px; color: #888; margin: 0; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.token-price { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 900; color: #FFD700; flex-shrink: 0; background: rgba(0,0,0,0.5); padding: 4px 10px; border-radius: 8px; border: 1px solid rgba(255,215,0,0.2); }

/* Premium Breakdown Box */
.breakdown-detail { background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; padding: 20px; margin: 20px 0; text-align: left; }
.breakdown-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 13px; font-weight: 600; }
.breakdown-row.border { border-bottom: 1px dashed rgba(255,255,255,0.1); margin-bottom: 8px; padding-bottom: 12px; }
.breakdown-label { color: #888; text-transform: uppercase; letter-spacing: 0.05em; font-size: 11px; }
.breakdown-value { color: #FFF; font-family: 'JetBrains Mono', monospace; }
.breakdown-value.negative { color: #F87171; }
.breakdown-value.positive { color: #4ADE80; font-size: 14px; text-shadow: 0 0 10px rgba(74,222,128,0.3); }

/* Modern Instructions */
.mmg-instructions { background: rgba(0,255,255,0.03); border-left: 3px solid #00FFFF; border-radius: 0 12px 12px 0; padding: 16px; margin: 20px 0; font-size: 12px; text-align: left; line-height: 1.6; color: #CCC; }
.mmg-instructions p { margin: 0 0 8px 0; }
.mmg-instructions p:last-child { margin: 0; }
.mmg-instructions strong { color: #00FFFF; font-family: 'JetBrains Mono', monospace; }

/* THE GLASSMORPHIC EARNINGS BUTTON */
        .earnings-btn {
            width: 100%; padding: 16px; border-radius: 14px; font-size: 14px; font-weight: 900; cursor: pointer; transition: all 0.2s ease-out; text-transform: uppercase; letter-spacing: 0.05em;
            background: rgba(255, 215, 0, 0.04); 
            border: 1px solid rgba(255, 215, 0, 0.25); 
            color: #FFD700; 
            backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
            box-shadow: 0 4px 15px rgba(255, 215, 0, 0.05);
        }
        .earnings-btn:hover:not(:disabled) {
            background: rgba(255, 215, 0, 0.1);
            border-color: rgba(255, 215, 0, 0.5);
            box-shadow: 0 0 15px rgba(255, 215, 0, 0.15);
        }
        .earnings-btn:active:not(:disabled) {
            background: #FFD700; color: #000; border-color: #FFD700; box-shadow: 0 0 30px rgba(255,215,0,0.7); transform: scale(0.98);
        }
        .earnings-btn:disabled { opacity: 0.35; cursor: not-allowed; border-color: rgba(255,255,255,0.05); color: #666; background: rgba(255,255,255,0.02); }

        .anon-toggle { display: flex; align-items: center; gap: 10px; margin: 16px 0; cursor: pointer; padding: 12px; background: rgba(255,255,255,0.02); border-radius: 12px; border: 1px solid transparent; transition: border 0.2; }
.anon-toggle:hover { border: 1px solid rgba(255,255,255,0.1); }
.anon-toggle span { font-size: 12px; color: #AAA; font-weight: 600; }

/* Sleek Submit Button */
.submit-btn { width: 100%; padding: 16px; background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); color: #000; border: none; border-radius: 14px; font-size: 14px; font-weight: 900; cursor: pointer; transition: all 0.2s; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 8px; box-shadow: 0 10px 20px rgba(255,215,0,0.2); }
.submit-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 25px rgba(255,215,0,0.3); }
.submit-btn.cancel-btn { background: #1A1A1A; color: #FFF; border: 1px solid #333; box-shadow: none; }
.submit-btn.cancel-btn:hover { background: #222; border-color: #444; }
.success-state { text-align: center; padding: 30px 20px; }
.success-check { width: 64px; height: 64px; background: rgba(74, 222, 128, 0.1); border: 2px solid #4ADE80; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 28px; color: #4ADE80; box-shadow: 0 0 30px rgba(74,222,128,0.2); }
            `}</style>
             <div className={`w-full md:max-w-[1150px] md:max-h-[95vh] md:rounded-2xl overflow-hidden relative flex flex-col shadow-2xl`} style={{ height: '100dvh', maxHeight: '100dvh', border: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button" className="closeButton" onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>
                
                {/* The video section wraps your aspect ratio tightly at the top with NO vertical letterbox voids [1.1.2] */}
                <div className="w-full flex-shrink-0 flex justify-center items-center bg-black overflow-hidden" style={{ aspectRatio: isVertical && !isLocked ? '9 / 16' : '16 / 9' }}>
                    <div style={{ width: '100%', height: '100%' }}>
                        {memoizedPlayer}
                    </div>
                </div>
                
                {/* The info and comments section now consumes the entire remaining vertical screen, stretching upwards [1.1.2]! */}
                <div className="premium-glass-info w-full flex-1 flex flex-col min-h-0" style={{ overflow: 'hidden', padding: 0 }}>
                    
                    {/* 1. SNAPPED HEADER: Title & Share (Padded locally, No cutoff possible) */}
                    <div className="flex justify-between items-start gap-4 flex-shrink-0" style={{ padding: '16px 16px 12px 16px' }}>
                        <h2 className="m-0 text-lg text-white font-bold leading-tight flex-1" style={{ letterSpacing: '0.015em' }}>
                            {liveContentItem?.title}
                        </h2>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                type="button"
                                onClick={handleShareVideo}
                                className="bg-[#2A2A2A] border border-solid border-white/10 hover:border-white/20 rounded-full w-9 h-9 flex items-center justify-center cursor-pointer text-white transition-colors"
                                title="Share Video"
                            >
                                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/></svg>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('openReportModal', { detail: liveContentItem })); }}
                                className="bg-[#2A2A2A] border border-solid border-white/10 hover:border-white/20 rounded-full w-9 h-9 flex items-center justify-center cursor-pointer text-white transition-colors"
                                title="More options"
                            >
                                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg>
                            </button>
                        </div>
                    </div>

                    {/* 2. SCROLLABLE AREA: Creator Info, Description, and Comments Area */}
                    <div className="overflow-y-auto flex-1" style={{ padding: '0 16px 16px 16px', overflowX: 'hidden' }}>
                        
                        {/* Creator Info & Action Row (Non-Colliding Mobile Flex Layout) */}
                        <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                            <div 
                                className="flex items-center gap-2.5 cursor-pointer min-w-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    executeSafeNavigation('navigateToUserProfile', { userId: liveContentItem.creatorId });
                                }}
                            >
                                <img 
                                    src={creatorProfile?.profilePictureUrl || liveContentItem?.creatorProfilePictureUrl || contentItem?.creatorProfilePictureUrl || 'https://placehold.co/40x40/555/FFF?text=P'} 
                                    alt="Uploader Avatar" 
                                    className="w-10 h-10 rounded-full object-cover border border-solid border-white/10 flex-shrink-0"
                                />
                                <div className="min-w-0">
                                    <div className="m-0 text-sm text-white font-bold flex items-center flex-wrap gap-1">
                                        <span className="truncate max-w-[150px] sm:max-w-[200px]" style={{ fontSize: '14px' }}>
                                            {creatorProfile?.creatorName || liveContentItem?.creatorName || contentItem?.creatorName || 'NVA Artist'}
                                        </span>
                                        <RoleBadge profile={creatorProfile || { isFilmClub: liveContentItem?.isFilmClub, isContestant: liveContentItem?.isContestant }} />
                                    </div>
                                </div>
                            </div>

                            {currentUser && liveContentItem?.id && !liveContentItem.isPromotion && (
                                <div className="flex items-center gap-2 flex-wrap justify-end ml-auto">
                                    {liveContentItem.monetizationStatus === 'approved' && (
                                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255, 69, 0, 0.1)', border: '1px solid #FF4500', borderRadius: '8px', overflow: 'visible', boxShadow: '0 0 10px rgba(255, 69, 0, 0.3)', position: 'relative' }}>
                                                {/* TikTok Style Glowing Combo Badge */}
                                                {comboCount > 1 && (
                                                    <div className="tiktok-combo-badge">
                                                        x{comboCount}
                                                    </div>
                                                )}
                                                <button 
                                                    type="button"
                                                    onClick={handleQuickTip}
                                                    style={{ background: 'transparent', color: '#FFF', fontWeight: '900', padding: '0 10px', height: '36px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', border: 'none', borderRight: '1px solid rgba(255, 69, 0, 0.4)', transition: 'all 0.2s', position: 'relative', overflow: 'visible' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 69, 0, 0.2)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                >
                                                {/* Flame-Engulfed Coin SVG: Flame extends above top boundary */}
                                                <svg className={flameAnimId === 'quick_tip' ? "flame-burst-anim" : ""} viewBox="0 0 24 28" width="20" height="24" style={{ overflow: 'visible', filter: 'drop-shadow(0 2px 6px rgba(255,69,0,0.8))' }}>
                                                    <defs>
                                                        <linearGradient id="flameGradCoin" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#FFD700"/>
                                                            <stop offset="50%" stopColor="#FF4500"/>
                                                            <stop offset="100%" stopColor="#8B0000"/>
                                                        </linearGradient>
                                                    </defs>
                                                    {/* Coin Circle Base */}
                                                    <circle cx="12" cy="17" r="8" fill="#B8860B" stroke="#FFD700" strokeWidth="1.2" />
                                                    <circle cx="12" cy="17" r="6" fill="none" stroke="#FFE55C" strokeWidth="0.8" strokeDasharray="1.5 1.5" />
                                                    {/* Engulfing Flame extending above coin boundary */}
                                                    <path d="M12 1 C9.5 5 7 8 7 14 C7 18.5 9.2 21 12 21 C14.8 21 17 18.5 17 14 C17 8 14.5 5 12 1 Z M12 9 C13.5 12 14.5 14 14.5 16 C14.5 18 13.4 19 12 19 C10.6 19 9.5 18 9.5 16 C9.5 14 10.5 12 12 9 Z" fill="url(#flameGradCoin)" />
                                                </svg>
                                                <span style={{ fontSize: '12px', letterSpacing: '0.08em' }}>TIP</span>
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    window.dispatchEvent(new CustomEvent('openGlobalTipping', {
                                                        detail: {
                                                            targetUser: { id: liveContentItem.creatorId, creatorName: liveContentItem.creatorName || creatorProfile?.creatorName || "Creator" },
                                                            viewerProfile: viewerProfile,
                                                            context: { eventId: itemType === 'event' ? liveContentItem.id : null }
                                                        }
                                                    }));
                                                }}
                                                style={{ background: 'transparent', color: '#FFD700', padding: '0 8px', height: '36px', display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', border: 'none', transition: 'all 0.2s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 69, 0, 0.2)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <span style={{ fontSize: '16px' }}>🏅</span>
                                                <span style={{ fontSize: '10px', color: '#FF4500', textShadow: '0 0 5px #FF4500' }}>▾</span>
                                            </button>
                                        </div>
                                    )}
                                    <div style={{ position: 'relative' }} onClick={(e) => {
                                        const heart = document.createElement('div');
                                        heart.className = 'floating-heart';
                                        heart.innerText = ['❤️', '🔥', '👍'][Math.floor(Math.random() * 3)];
                                        e.currentTarget.appendChild(heart);
                                        setTimeout(() => heart.remove(), 1000);
                                    }}>
                                        <LikeButton contentItem={{ ...liveContentItem, id: liveContentItem.originalContentId || liveContentItem.id }} currentUser={currentUser} showMessage={showMessage} itemType={liveContentItem.originalContentId ? 'event' : itemType} />
                                    </div>
                                    <div className="bg-[#111] border border-solid border-white/10 rounded-full flex items-center text-white gap-1.5 px-3 h-9">
                                        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"></path></svg>
                                        <span>{(liveContentItem?.commentCount || comments.length).toLocaleString()}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                    {liveContentItem?.description && (
                        <div 
                            className="bg-[#2A2A2A] p-3 rounded-xl cursor-pointer"
                            onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                        >
                            {!liveContentItem.isPromotion && (
                                <p className="m-0 mb-2 text-sm text-white font-bold">
                                    {(displayViewCount || 0).toLocaleString()} Views
                                </p>
                            )}
                            <p className={`m-0 text-sm text-[#DDDDDD] leading-normal whitespace-pre-wrap ${!descriptionExpanded && 'line-clamp-2'}`}>
                                {liveContentItem.description}
                            </p>
                            {(liveContentItem.description.length > 100) && (
                                <span className="text-[#AAAAAA] text-xs font-bold mt-1 inline-block">
                                    {descriptionExpanded ? 'Show less' : '...more'}
                                </span>
                            )}
                        </div>
                    )}

                    {/* SCROLLABLE COMMENTS AREA (Now fully stretched upwards to consume empty space!) */}
                    <div style={{ marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '15px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '900', color: '#FFD700', textTransform: 'uppercase', letterSpacing: '1px' }}>Audience Thoughts</p>
                        <div style={{ flex: 1, background: '#0F0F0F', borderRadius: '8px', padding: '16px 12px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
                            {(() => {
                                if (comments.length === 0) return <p style={{ color: '#666', fontSize: '12px', margin: 'auto 0', textAlign: 'center' }}>No comments yet. Share your thoughts below!</p>;

                                const repliesMap = {};
                                const rootComments = [];
                                
                                // Process timeline logic so parent threads and replies read naturally chronological 
                                const sortedComments = [...comments].sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));

                                sortedComments.forEach(comment => {
                                    let parentId = comment.replyTo?.id || (typeof comment.replyTo === 'string' ? comment.replyTo : null);
                                    if (parentId) {
                                        if (!repliesMap[parentId]) repliesMap[parentId] = [];
                                        repliesMap[parentId].push(comment);
                                    }
                                });

                                sortedComments.forEach(comment => {
                                    let parentId = comment.replyTo?.id || (typeof comment.replyTo === 'string' ? comment.replyTo : null);
                                    if (!parentId || !sortedComments.some(c => c.id === parentId)) {
                                        rootComments.push(comment);
                                    }
                                });

                                // Latest root comments at the top (YouTube format)
                                rootComments.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

                                const renderCommentUI = (comment, isReply = false) => {
                                    let timeString = 'Just now';
                                    if (comment.createdAt) {
                                        const date = comment.createdAt.toDate ? comment.createdAt.toDate() : new Date(comment.createdAt);
                                        const diff = Math.floor((new Date() - date) / 1000);
                                        if (diff > 86400) timeString = `${Math.floor(diff / 86400)}d ago`;
                                        else if (diff > 3600) timeString = `${Math.floor(diff / 3600)}h ago`;
                                        else if (diff > 60) timeString = `${Math.floor(diff / 60)}m ago`;
                                    }

                                    const threadReplies = repliesMap[comment.id] || [];

                                    return (
                                        <div key={comment.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: isReply ? '12px' : '0' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '13px', lineHeight: '1.4' }}>
                                                <img 
                                                    src={comment.userProfilePicture || 'https://placehold.co/40x40/333/FFF?text=U'} 
                                                    alt="Avatar" 
                                                    style={{ width: isReply ? '28px' : '36px', height: isReply ? '28px' : '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}
                                                    onClick={(e) => { e.stopPropagation(); executeSafeNavigation('navigateToUserProfile', { userId: comment.userId }); }}
                                                />
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span 
                                                            style={{ fontWeight: 'bold', color: generateColorFromId(comment.userId), cursor: 'pointer', fontSize: '13px' }} 
                                                            onClick={(e) => { e.stopPropagation(); executeSafeNavigation('navigateToUserProfile', { userId: comment.userId }); }}
                                                        >
                                                            @{comment.userName.replace(/\s+/g, '').toLowerCase()}
                                                        </span>
                                                        <span style={{ color: '#888', fontSize: '12px' }}>{timeString}</span>
                                                    </div>
                                                    <div style={{ color: '#E0E0E0', fontSize: '14px', wordBreak: 'break-word', marginTop: '2px' }}>
                                                        {isReply && comment.replyTo?.userName && <span style={{ color: '#00FFFF', fontWeight: 'bold', marginRight: '6px' }}>@{comment.replyTo.userName.replace(/\s+/g, '').toLowerCase()}</span>}
                                                        {comment.text}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '6px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#AAA' }}>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleLikeComment(comment.id); }}
                                                                style={{ background: 'none', border: 'none', color: comment.likedBy?.includes(currentUser?.uid) ? '#3EA6FF' : 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: 0, transition: 'color 0.2s' }} 
                                                                title={comment.likedBy?.includes(currentUser?.uid) ? "Unlike" : "Like"}
                                                            >
                                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"></path></svg>
                                                                {comment.likedBy?.length > 0 ? comment.likedBy.length : ''}
                                                            </button>
                                                            <button style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'not-allowed', display: 'flex', alignItems: 'center', fontSize: '12px', padding: 0, opacity: 0.5 }} title="Dislike (Placeholder)">
                                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"></path></svg>
                                                            </button>
                                                        </div>
                                                        <button 
                                                            onClick={() => setReplyingTo({ id: comment.id, userName: comment.userName, userId: comment.userId })}
                                                            style={{ background: 'none', border: 'none', color: '#FFF', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', padding: '4px 12px', borderRadius: '16px' }}
                                                            onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                                                            onMouseOut={(e) => e.target.style.background = 'none'}
                                                        >
                                                            Reply
                                                        </button>
                                                    </div>
                                                </div>
                                                {(currentUser?.uid === comment.userId || currentUser?.uid === liveContentItem?.creatorId || viewerProfile?.role === 'admin' || viewerProfile?.role === 'authority') && (
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment.id); }}
                                                        style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '8px', fontSize: '16px', flexShrink: 0, opacity: 0.6, borderRadius: '50%' }}
                                                        title="Delete Comment"
                                                        onMouseOver={(e) => { e.target.style.opacity = 1; e.target.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                                                        onMouseOut={(e) => { e.target.style.opacity = 0.6; e.target.style.background = 'none'; }}
                                                    >
                                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"></path></svg>
                                                    </button>
                                                )}
                                            </div>

                                            {/* Nested Dropdown Button for Replies */}
                                            {threadReplies.length > 0 && !isReply && (
                                                <div style={{ marginLeft: '48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {/* Automatically show the very first reply */}
                                                    {renderCommentUI(threadReplies[0], true)}
                                                    
                                                    {/* Toggle for the remaining replies */}
                                                    {threadReplies.length > 1 && (
                                                        <>
                                                            <button 
                                                                onClick={(e) => toggleThread(comment.id, e)}
                                                                style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: '#3EA6FF', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', padding: '6px 12px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                                onMouseOver={(e) => e.target.style.background = 'rgba(62, 166, 255, 0.1)'}
                                                                onMouseOut={(e) => e.target.style.background = 'none'}
                                                            >
                                                                {expandedThreads[comment.id] ? '▴ Hide previous replies' : `▾ View ${threadReplies.length - 1} more replies`}
                                                            </button>
                                                            
                                                            {expandedThreads[comment.id] && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                                    {threadReplies.slice(1).map(reply => renderCommentUI(reply, true))}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                };

                                return (
                                    <>
                                        {rootComments.map(c => renderCommentUI(c, false))}
                                        {comments.length >= commentLimit && (
                                            <button 
                                                onClick={() => setCommentLimit(prev => prev + 10)}
                                                style={{ padding: '12px', background: 'rgba(255,255,255,0.05)', color: '#FFF', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginTop: '8px', textAlign: 'center' }}
                                                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                                                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                                            >
                                                Load more comments...
                                            </button>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* FIXED FOOTER: The Input section stays pinned to the bottom */}
                {currentUser && (
                    <div style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.08)', background: 'transparent', padding: '12px 16px' }}>
                        {replyingTo && (
                            <div style={{ background: 'rgba(0, 255, 255, 0.08)', border: '1px solid rgba(0, 255, 255, 0.3)', color: '#00FFFF', fontSize: '11px', fontWeight: '900', padding: '4px 10px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <span>💬 Replying to @{replyingTo.userName}</span>
                                <button type="button" onClick={() => setReplyingTo(null)} style={{ background: 'none', border: 'none', color: '#00FFFF', cursor: 'pointer', padding: 0, marginLeft: '6px' }}>✕</button>
                            </div>
                        )}
                        {showEmojiPicker && (
                            <div style={{ position: 'absolute', bottom: '70px', left: '16px', right: '16px', height: '250px', background: '#1F1F1F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', zIndex: 100, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(36px, 1fr))', gap: '4px', padding: '10px', overflowY: 'auto', boxShadow: '0 -10px 30px rgba(0,0,0,0.5)' }}>
                                {EMOJI_LIST.map((emoji, idx) => (
                                    <button
                                        key={`${emoji}-${idx}`}
                                        type="button"
                                        onClick={() => setNewCommentText(prev => prev + emoji)}
                                        style={{ background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '8px' }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#1F1F1F', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '0 10px' }}>
                                <button 
                                    type="button"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                    style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '8px 4px', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    😀
                                </button>
                                <input 
                                    id="commentsInput"
                                    type="text" 
                                    placeholder="Add to the conversation..." 
                                    style={{ flex: 1, background: 'transparent', border: 'none', color: '#FFF', padding: '10px 8px', fontSize: '14px', outline: 'none' }}
                                    value={newCommentText}
                                    onChange={(e) => setNewCommentText(e.target.value)}
                                    onFocus={() => setShowEmojiPicker(false)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSubmitComment();
                                            setShowEmojiPicker(false);
                                        }
                                    }}
                                />
                            </div>
                            <button 
                                className="button" 
                                style={{ margin: 0, padding: '0', width: '40px', height: '40px', background: '#FFD700', color: '#000', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => { handleSubmitComment(); setShowEmojiPicker(false); }}
                                disabled={isSubmitting || !newCommentText.trim()}
                            >
                                {isSubmitting ? '...' : (
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
            
            {/* Legacy Tip Catalog Migrated to GlobalTippingModal */}

            {/* Global Token Vault Overlay */}
            <RoastTokenVault 
                isOpen={isVaultOpen} 
                onClose={() => setIsVaultOpen(false)} 
                currentUser={currentUser}
                creatorProfile={viewerProfile}
                showMessage={showMessage}
            />
        </div>
    );
};

export default VideoPlayerModal;